import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL } from '../../config';
import { User, AISession, MemoryFact, ResponseStyle } from '../../types';
import DailyBiometrics from './DailyBiometrics';
import './AICoachSettings.css';

interface AICoachSettingsProps {
  user: User;
  onSessionSelect: (sessionId: number) => void;
}

const STYLES: ResponseStyle[] = ['pogacar_calm_pro', 'clarkson_topgear', 'trump_rally'];

interface GCalSettings {
  connected: boolean;
  sync_enabled: boolean;
  calendar_id: string | null;
}

const AICoachSettings: React.FC<AICoachSettingsProps> = ({ user, onSessionSelect }) => {
  const { t } = useLanguage();
  const [responseStyle, setResponseStyle] = useState<ResponseStyle>(
    () => (localStorage.getItem('ai_coach_response_style') as ResponseStyle) || 'pogacar_calm_pro'
  );
  const [sessions, setSessions] = useState<AISession[]>([]);
  const [facts, setFacts] = useState<MemoryFact[]>([]);
  const [editingFact, setEditingFact] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');
  const [gcal, setGcal] = useState<GCalSettings | null>(null);
  const [gcalLoading, setGcalLoading] = useState(false);

  useEffect(() => {
    fetchSessions();
    fetchFacts();
    fetchGcalSettings();
  }, []);

  const fetchSessions = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/ai/sessions`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSessions(Array.isArray(data) ? data : []);
      }
    } catch (e) { console.error('Failed to load sessions:', e); }
  };

  const fetchFacts = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/ai/memory/facts`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setFacts(data.facts || []);
      }
    } catch (e) { console.error('Failed to load facts:', e); }
  };

  const handleStyleChange = (style: ResponseStyle) => {
    setResponseStyle(style);
    localStorage.setItem('ai_coach_response_style', style);
  };

  const handleNewSession = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/ai/sessions/new`, {
        method: 'POST',
        credentials: 'include',
      });
      fetchSessions();
    } catch (e) { console.error('Failed to create session:', e); }
  };

  const handleFactAction = async (factId: number, action: 'confirm' | 'reject') => {
    try {
      await fetch(`${API_BASE_URL}/api/ai/memory/facts/${factId}/${action}`, {
        method: 'POST',
        credentials: 'include',
      });
      fetchFacts();
    } catch (e) { console.error(`Failed to ${action} fact:`, e); }
  };

  const handleDeleteFact = async (factId: number) => {
    try {
      await fetch(`${API_BASE_URL}/api/ai/memory/facts/${factId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      fetchFacts();
    } catch (e) { console.error('Failed to delete fact:', e); }
  };

  const handleCorrectFact = async (factId: number) => {
    if (!editContent.trim()) return;
    try {
      await fetch(`${API_BASE_URL}/api/ai/memory/facts/${factId}/correct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: editContent.trim() }),
      });
      setEditingFact(null);
      setEditContent('');
      fetchFacts();
    } catch (e) { console.error('Failed to correct fact:', e); }
  };

  const handleForgetAll = async () => {
    if (!window.confirm(t('aiCoach.settings.forgetAllConfirm'))) return;
    try {
      await fetch(`${API_BASE_URL}/api/ai/memory/facts`, {
        method: 'DELETE',
        credentials: 'include',
      });
      fetchFacts();
    } catch (e) { console.error('Failed to forget all:', e); }
  };

  const fetchGcalSettings = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/google-calendar/settings`, { credentials: 'include' });
      if (res.ok) setGcal(await res.json());
    } catch { /* silent */ }
  };

  const handleGcalConnect = () => {
    window.location.href = `${API_BASE_URL}/auth/google-calendar/connect`;
  };

  const handleGcalDisconnect = async () => {
    setGcalLoading(true);
    try {
      await fetch(`${API_BASE_URL}/auth/google-calendar/disconnect`, { method: 'POST', credentials: 'include' });
      setGcal({ connected: false, sync_enabled: false, calendar_id: null });
    } catch { /* silent */ }
    setGcalLoading(false);
  };

  const handleGcalToggleSync = async () => {
    if (!gcal) return;
    setGcalLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/google-calendar/settings`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sync_enabled: !gcal.sync_enabled }),
      });
      if (res.ok) {
        const data = await res.json();
        setGcal(prev => prev ? { ...prev, ...data } : data);
      }
    } catch { /* silent */ }
    setGcalLoading(false);
  };

  const formatSessionDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return dateStr; }
  };

  return (
    <div className="ai-settings">
      {/* Response Style */}
      <div className="ai-settings-section">
        <h3>{t('aiCoach.settings.responseStyle')}</h3>
        <div className="ai-settings-styles">
          {STYLES.map(style => (
            <div
              key={style}
              className={`ai-settings-style-option ${responseStyle === style ? 'active' : ''}`}
              onClick={() => handleStyleChange(style)}
            >
              <div className="ai-settings-style-radio">
                {responseStyle === style && <div className="ai-settings-style-radio-dot" />}
              </div>
              <div className="ai-settings-style-info">
                <div className="ai-settings-style-name">{t(`aiCoach.settings.styles.${style}`)}</div>
                <div className="ai-settings-style-desc">{t(`aiCoach.settings.styleDescriptions.${style}`)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Memory Facts */}
      <div className="ai-settings-section">
        <h3>{t('aiCoach.settings.memory')}</h3>
        {facts.length === 0 ? (
          <p className="ai-settings-empty">{t('aiCoach.settings.noFacts')}</p>
        ) : (
          <div className="ai-settings-facts">
            {facts.map(fact => (
              <div key={fact.id} className="ai-settings-fact-item">
                <div style={{ flex: 1 }}>
                  {editingFact === fact.id ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        value={editContent}
                        onChange={e => setEditContent(e.target.value)}
                        style={{ flex: 1, padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}
                        autoFocus
                      />
                      <button onClick={() => handleCorrectFact(fact.id)} style={{ padding: '4px 10px', background: '#374151', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>OK</button>
                      <button onClick={() => setEditingFact(null)} style={{ padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, cursor: 'pointer', background: '#fff' }}>X</button>
                    </div>
                  ) : (
                    <>
                      <div className="ai-settings-fact-content">{fact.content}</div>
                      <div className="ai-settings-fact-category">{fact.category}</div>
                    </>
                  )}
                </div>
                <span className={`ai-settings-fact-status ${fact.status === 'pending_confirmation' ? 'pending' : 'confirmed'}`}>
                  {fact.status === 'pending_confirmation' ? t('aiCoach.settings.pending') : t('aiCoach.settings.confirmed')}
                </span>
                <div className="ai-settings-fact-actions">
                  {fact.status === 'pending_confirmation' && (
                    <>
                      <button className="confirm" onClick={() => handleFactAction(fact.id, 'confirm')}>{t('aiCoach.settings.confirmFact')}</button>
                      <button onClick={() => handleFactAction(fact.id, 'reject')}>{t('aiCoach.settings.rejectFact')}</button>
                    </>
                  )}
                  <button onClick={() => { setEditingFact(fact.id); setEditContent(fact.content); }}>{t('aiCoach.settings.correctFact')}</button>
                  <button className="danger" onClick={() => handleDeleteFact(fact.id)}>{t('aiCoach.settings.deleteFact')}</button>
                </div>
              </div>
            ))}
          </div>
        )}
        {facts.length > 0 && (
          <button className="ai-settings-forget-all" onClick={handleForgetAll}>
            {t('aiCoach.settings.forgetAll')}
          </button>
        )}
      </div>

      {/* Google Calendar Sync */}
      <div className="ai-settings-section">
        <h3>Google Calendar</h3>
        <p className="ai-settings-gcal-desc">One-way sync: workouts from AlpsEpics appear in your Google Calendar.</p>
        {gcal ? (
          gcal.connected ? (
            <div className="ai-settings-gcal">
              <div className="ai-settings-gcal-status">
                <span className="ai-settings-gcal-connected">Connected</span>
                {gcal.calendar_id && <span className="ai-settings-gcal-id">{gcal.calendar_id}</span>}
              </div>
              <div className="ai-settings-gcal-toggle">
                <label className="ai-settings-toggle-label">
                  <input
                    type="checkbox"
                    checked={gcal.sync_enabled}
                    onChange={handleGcalToggleSync}
                    disabled={gcalLoading}
                  />
                  <span>Auto-sync workouts</span>
                </label>
                <span className="ai-settings-gcal-sync-status">
                  {gcal.sync_enabled ? 'Syncing' : 'Paused'}
                </span>
              </div>
              <button className="ai-settings-gcal-disconnect" onClick={handleGcalDisconnect} disabled={gcalLoading}>
                Disconnect
              </button>
            </div>
          ) : (
            <button className="ai-settings-gcal-connect" onClick={handleGcalConnect}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              Connect Google Calendar
            </button>
          )
        ) : (
          <p className="ai-settings-empty">Loading...</p>
        )}
      </div>

      {/* Daily Biometrics */}
      <DailyBiometrics />

      {/* Session History */}
      <div className="ai-settings-section">
        <h3>{t('aiCoach.settings.sessions')}</h3>
        {sessions.length === 0 ? (
          <p className="ai-settings-empty">{t('aiCoach.settings.noSessions')}</p>
        ) : (
          <div className="ai-settings-sessions">
            {sessions.map(s => (
              <div key={s.id} className="ai-settings-session-item" onClick={() => onSessionSelect(s.id)}>
                <span className="ai-settings-session-summary">
                  {s.summary || `Session #${s.id}`}
                </span>
                <span className="ai-settings-session-meta">
                  {formatSessionDate(s.started_at)} | {t('aiCoach.settings.messages', { count: s.message_count })}
                </span>
                {s.is_active && <span className="ai-settings-session-badge">{t('aiCoach.settings.active')}</span>}
              </div>
            ))}
          </div>
        )}
        <button className="ai-settings-new-session" onClick={handleNewSession}>
          + {t('aiCoach.settings.newSession')}
        </button>
      </div>
    </div>
  );
};

export default AICoachSettings;
